import React, { useEffect, useState, useRef } from 'react';
import styled from '@emotion/styled/macro';
import InfoIcon from '../icon-atoms/info-icon-atom';
import Translate from '@utils/translation-functions';
import { findIndex, pullAt } from 'lodash';
import { FlexibleLinkAtom } from '../link-atoms/link-atoms';
import vars from '../../variables';
import {
  TermProps,
  ParagraphProps,
  GlossaryTooltipTextProps,
  TooltipBoxProps,
} from './glossay-tooltips.types';

export const TooltipText = styled.span`
  cursor: help;
  border-bottom: 1px dashed ${vars.coreDarkGrey};
`;

const TooltipBox = styled.span<TooltipBoxProps>`
  background-color: #f2f2f2 !important;
  box-shadow:
    0px 12px 16px -4px rgba(16, 24, 40, 0.08),
    0px 4px 6px -2px rgba(16, 24, 40, 0.03);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 12px;
  gap: 4px;
  position: absolute;
  left: ${({ tooltipPosition }) => tooltipPosition.x}px;
  top: ${({ tooltipPosition }) => tooltipPosition.y + 25}px;
  z-index: 99999;
  width: auto;
  max-width: 320px;
  visibility: hidden;
  cursor: help;
  transition: visibility ease-out 0.1s;
  &:hover {
    visibility: visible;
  }
  & .tooltip-label,
  & .tooltip-definition,
  & .tooltip-link {
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.0015em;
    background-color: #f2f2f2 !important;
  }
  & .tooltip-label {
    font-weight: ${vars.fontWeightBold};
    text-transform: capitalize;
    color: ${vars.coreDarkGrey} !important;
  }
  & .tooltip-definition {
    font-weight: ${vars.fontWeightRegular};
  }
  .tooltip-link-label {
    font-family: 'Arial', sans-serif;
  }
  & dfn {
    font-style: normal;
  }
`;

const Highlight = styled.span`
  & ${TooltipText}:hover + ${TooltipBox} {
    visibility: visible;
  }
`;

const TooltipImg = styled.img`
  min-width: 100%;
  height: 100%;
  margin: 0 0 0 0 !important;
`;

const Term: React.FC<TermProps> = ({
  children,
  definition,
  definitionImage,
  definitionLink,
  usedTooltipKeywords,
}) => {
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const highlightRef = useRef<HTMLDivElement | null>(null);

  const setPositionState = () => {
    const windowWidth = window.innerWidth;

    if (windowWidth <= 480) {
      setTooltipPosition({
        x: 15,
        y: highlightRef.current?.offsetTop ?? 0,
      });
    } else if (windowWidth > 480 && windowWidth <= 800) {
      setTooltipPosition({
        x: (highlightRef.current?.offsetLeft ?? 0) - 46,
        y: highlightRef.current?.offsetTop ?? 0,
      });
    } else {
      setTooltipPosition({
        x: (highlightRef.current?.offsetLeft ?? 0) - 11,
        y: highlightRef.current?.offsetTop ?? 0,
      });
    }
  };

  useEffect(() => {
    window.addEventListener('resize', setPositionState);

    return () => {
      window.removeEventListener('resize', setPositionState);
    };
  }, []);

  useEffect(() => {
    return () => {
      //when unmounting the highlight component, the used terms list should have this term removed from it.
      const termIndex = findIndex(usedTooltipKeywords, usedKeyword =>
        new RegExp(`^${children}$`, 'i').test(usedKeyword)
      );

      pullAt(usedTooltipKeywords, [termIndex]);
    };
  }, [children, usedTooltipKeywords]);

  useEffect(() => {
    setPositionState();
  }, [highlightRef, setTooltipPosition]);

  const tooltipFontFamily = { fontFamily: "'Arial', sans-serif" };

  return (
    <Highlight ref={highlightRef} onMouseEnter={setPositionState}>
      <TooltipText>
        <span data-testid="test-tooltip-term" aria-label="tooltip-term">
          {children}
        </span>
        <InfoIcon />
      </TooltipText>
      <TooltipBox
        data-testid="test-tooltip-box"
        aria-label="tooltip-box"
        tooltipPosition={tooltipPosition}
      >
        {definitionImage && (
          <TooltipImg
            src={
              typeof definitionImage === 'string'
                ? definitionImage
                : definitionImage?.file?.url
            }
          />
        )}
        <span className="tooltip-label" style={tooltipFontFamily}>
          {children}
        </span>
        <span className="tooltip-definition">
          <dfn style={tooltipFontFamily}>{definition}</dfn>
        </span>
        {definitionLink && (
          <span className="tooltip-link" data-testid="test-tooltip-link">
            <FlexibleLinkAtom
              className="tooltip-link-label"
              url={definitionLink}
            >
              {Translate('Read more', true)}
            </FlexibleLinkAtom>
          </span>
        )}
      </TooltipBox>
    </Highlight>
  );
};

const Paragraph: React.FC<ParagraphProps> = ({
  paragraph,
  keywords,
  termsForTooltip,
  usedTooltipKeywords,
}) => {
  let keyCount = 0;

  let keywordsRegex = keywords.join('\\b|\\b');
  let splitParagraph = paragraph.split(
    new RegExp(`\\b${keywordsRegex}\\b`, 'ig')
  );
  let paragraphMatches = paragraph?.match(
    new RegExp(`\\b${keywordsRegex}\\b`, 'ig')
  );
  let resultParagraph: (string | React.ReactNode)[] = [];

  for (let index = 0; index < splitParagraph.length; ++index) {
    resultParagraph.push(splitParagraph[index]);

    if (paragraphMatches && paragraphMatches[index] !== undefined) {
      const paragraphMatchesRegex = new RegExp(
        `^${paragraphMatches[index]}$`,
        'i'
      );

      const termsForTooltipIncludeParagraphMatch = termsForTooltip.some(term =>
        paragraphMatchesRegex.test(term.label)
      );
      const usedTooltipKeywordsIncludeParagraphMatch = usedTooltipKeywords.some(
        usedTerm => paragraphMatchesRegex.test(usedTerm)
      );

      if (
        index < splitParagraph.length - 1 &&
        termsForTooltipIncludeParagraphMatch
      ) {
        if (usedTooltipKeywordsIncludeParagraphMatch) {
          resultParagraph.push(paragraphMatches[index]);
        } else {
          const tooltip = termsForTooltip.find(term =>
            paragraphMatchesRegex.test(term.label)
          );

          resultParagraph.push(
            <Term
              key={++keyCount}
              usedTooltipKeywords={usedTooltipKeywords}
              definitionImage={
                typeof tooltip?.definitionImage === 'string'
                  ? tooltip?.definitionImage
                  : tooltip?.definitionImage?.file?.url
              }
              definition={tooltip?.definition?.definition ?? ''}
              definitionLink={tooltip?.definitionLink ?? undefined}
            >
              {paragraphMatches[index]}
            </Term>
          );

          usedTooltipKeywords.push(paragraphMatches[index]);
        }
      }
    }
  }

  return resultParagraph;
};

const GlossaryTooltipText: React.FC<GlossaryTooltipTextProps> = ({
  children,
  usedTooltipKeywords,
  termsForTooltip,
}) => {
  const keywords = termsForTooltip.map(term => term.label);
  return (
    <Paragraph
      paragraph={children}
      keywords={keywords}
      termsForTooltip={termsForTooltip}
      usedTooltipKeywords={usedTooltipKeywords}
    />
  );
};

export default GlossaryTooltipText;
